import {IconLeaf} from '@travel-frontend/icons';
import {Flex} from '@travel-frontend/ui-components';
import {noop} from 'lodash';
import {forwardRef, memo, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';

import {FilterHeadline} from '@/components/FilterHeadline';
import {FilterPlaceholder} from '@/components/FilterPlaceholder';
import {FilterStrip} from '@/components/FilterStrip';
import {PopularFilters} from '@/components/PopularFilters';
import {PromotionBadge} from '@/components/PromotionBadge';
import {config} from '@/config';
import {
  CategoryFilter,
  OptionFilter,
  PriceFilter,
  PriceFilterNew,
  RatingFilter,
} from '@/modules/common/components/Filter';
import {FilterSearch} from '@/modules/common/components/FilterSearch/FilterSearch';
import {getFiltersForSearch} from '@/modules/common/components/FilterSearch/utils/getFiltersForSearch';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {
  FILTER_BEACH,
  FILTER_BOARD_TYPE,
  FILTER_CHILDREN,
  FILTER_ENTERTAINMENT,
  FILTER_FACILITIES,
  FILTER_FLIGHT_TRANSFER,
  FILTER_HOTEL_CATEGORY,
  FILTER_HOTEL_RATING,
  FILTER_HOTEL_TYPE,
  FILTER_POOL,
  FILTER_PRICE,
  FILTER_PROMOTION_FLEX,
  FILTER_PROMOTION_SAFETY,
  FILTER_PROMOTION_SPECIALS,
  FILTER_RESTAURANT,
  FILTER_ROOM_ATTRIBUTES,
  FILTER_ROOM_TYPE,
  FILTER_SPORTS,
  FILTER_SUSTAINABILITY,
  FILTER_TOUR_OPERATOR,
  FILTER_WATER_SPORTS,
  FILTER_WELLNESS,
  filtersWithBestPrice,
  filtersWithOptionCount,
} from '@/modules/common/models/filters';
import {useFilters} from '@/modules/hotels/hooks/useFilters';
import {usePopularFilters} from '@/modules/hotels/hooks/usePopularFilters';
import {useAppSelector} from '@/redux/config';
import {
  EVENT_FILTER_REMOVE,
  EVENT_FILTER_SET,
} from '@/redux/middleware/googleTagManager';
import {
  areHotelsLoaded as getAreHotelsLoaded,
  getHotelCountFiltered,
  getHotelCountUnfiltered,
  haveHotelsFailedLoading as getHaveHotelsFailedLoading,
} from '@/redux/v1/hotels/list/reducer';
import {
  PROMOTION_CATEGORY_FLEX,
  PROMOTION_CATEGORY_SAFETY,
  PROMOTION_CATEGORY_SPECIAL,
} from '@/redux/v1/models';
import {testId} from '@/utils/testId';

const promotionCategoryByFilterId = {
  [FILTER_PROMOTION_SPECIALS]: PROMOTION_CATEGORY_SPECIAL,
  [FILTER_PROMOTION_FLEX]: PROMOTION_CATEGORY_FLEX,
  [FILTER_PROMOTION_SAFETY]: PROMOTION_CATEGORY_SAFETY,
};

const defaultExpandedFiltersArray = new Set<string>([
  FILTER_BOARD_TYPE,
  FILTER_ROOM_TYPE,
  FILTER_ROOM_ATTRIBUTES,
  FILTER_FACILITIES,
  FILTER_SUSTAINABILITY,
  FILTER_CHILDREN,
  FILTER_POOL,
  FILTER_BEACH,
  FILTER_HOTEL_TYPE,
  FILTER_FLIGHT_TRANSFER,
]);

type Props = {
  noBorders?: boolean;
  onChange?: () => void;
};

const ForwardedFilters = forwardRef<HTMLDivElement, Props>(
  function MemoizedFilters({onChange = noop, noBorders = false}, ref) {
    const dispatch = useDispatch();
    const {t} = useTranslation('filters');
    const {priceMinEnabled} = config.filters.price;

    const PriceFilterComponent = priceMinEnabled ? PriceFilterNew : PriceFilter;
    const currentPage = useCurrentPage();
    const areHotelsLoaded = useAppSelector(getAreHotelsLoaded);
    const haveHotelsFailedLoading = useAppSelector(getHaveHotelsFailedLoading);
    const {filters, hasFilters} = useFilters();
    const popularFilterItems = usePopularFilters(filters);
    const hotelCountUnfiltered = useAppSelector(getHotelCountUnfiltered);
    const hotelCountFiltered = useAppSelector(getHotelCountFiltered);
    // @ts-expect-error ...
    const handleChange = (filter: Filter, popular?: boolean) => (event) => {
      if (event.type === 'clear' || event.type === 'pop') {
        dispatch({
          type: EVENT_FILTER_REMOVE,
          payload: {filters: [[filter.id, event.value]], popular, currentPage},
        });
      } else if (event.type === 'set' || event.type === 'push') {
        dispatch({
          type: EVENT_FILTER_SET,
          payload: {filters: [[filter.id, event.value]], popular, currentPage},
        });
      }
      filter.setValue(event.value);
      onChange();
    };

    const filterSearchItems = useMemo(
      () => getFiltersForSearch(filters),
      [filters]
    );

    if (
      // hotel request was successful but no hotels were found and no filters are set in URL
      (areHotelsLoaded &&
        !hasFilters &&
        hotelCountUnfiltered === 0 &&
        hotelCountFiltered === 0) ||
      // or error on initial request
      (!areHotelsLoaded && haveHotelsFailedLoading)
    ) {
      return null;
    }

    // still loading
    if (!areHotelsLoaded) {
      return <FilterPlaceholder />;
    }

    return (
      <Flex ref={ref} spacing="16px" data-testid={testId('filter')}>
        <PopularFilters
          data-testid={testId(`filter-popular`)}
          items={popularFilterItems}
          handleChange={handleChange}
        />
        <FilterStrip noBorders={noBorders}>
          <FilterHeadline>{t('allFilters')}</FilterHeadline>
          <FilterSearch onChange={handleChange} items={filterSearchItems} />
          <Flex divider spacing="dividers/medium">
            {filters
              .filter(({available}) => available)
              .map((filter) => {
                switch (filter.id) {
                  case FILTER_PRICE: {
                    return (
                      <PriceFilterComponent
                        key={filter.id}
                        caption={t('headlines.price')}
                        value={filter.value}
                        priceMin={filter.priceMin}
                        onChange={handleChange(filter)}
                        data-testid={testId(`filter-${filter.id}`)}
                      />
                    );
                  }
                  case FILTER_HOTEL_CATEGORY: {
                    return (
                      <CategoryFilter
                        key={filter.id}
                        caption={t('headlines.category')}
                        options={filter.options}
                        canShowOptionBestPrice={filtersWithBestPrice.has(
                          filter.id
                        )}
                        data-testid={testId(`filter-${filter.id}`)}
                        onChange={handleChange(filter)}
                      />
                    );
                  }
                  case FILTER_HOTEL_RATING: {
                    return (
                      <RatingFilter
                        key={filter.id}
                        caption={t('headlines.customerReviews')}
                        // @ts-expect-error ...
                        options={filter.options}
                        canShowOptionBestPrice={filtersWithBestPrice.has(
                          filter.id
                        )}
                        data-testid={testId(`filter-${filter.id}`)}
                        onChange={handleChange(filter)}
                      />
                    );
                  }
                  case FILTER_PROMOTION_SPECIALS:
                  case FILTER_PROMOTION_FLEX:
                  case FILTER_PROMOTION_SAFETY: {
                    return (
                      <OptionFilter
                        key={filter.id}
                        caption={
                          <>
                            <PromotionBadge
                              category={promotionCategoryByFilterId[filter.id]}
                              size="large"
                              isCompact
                            />
                            {filter.title}
                          </>
                        }
                        options={filter.options}
                        canShowOptionCount={filtersWithOptionCount.has(
                          filter.id
                        )}
                        canShowOptionBestPrice={filtersWithBestPrice.has(
                          filter.id
                        )}
                        data-testid={testId(`filter-${filter.id}`)}
                        onChange={handleChange(filter)}
                      />
                    );
                  }
                  case FILTER_FLIGHT_TRANSFER:
                  case FILTER_BOARD_TYPE:
                  case FILTER_ROOM_TYPE:
                  case FILTER_ROOM_ATTRIBUTES:
                  case FILTER_TOUR_OPERATOR:
                  case FILTER_FACILITIES:
                  case FILTER_CHILDREN:
                  case FILTER_WELLNESS:
                  case FILTER_POOL:
                  case FILTER_BEACH:
                  case FILTER_SPORTS:
                  case FILTER_WATER_SPORTS:
                  case FILTER_RESTAURANT:
                  case FILTER_ENTERTAINMENT:
                  case FILTER_HOTEL_TYPE: {
                    return (
                      <OptionFilter
                        key={filter.id}
                        caption={filter.title}
                        options={filter.options}
                        isCollapsable
                        isDefaultExpanded={defaultExpandedFiltersArray.has(
                          filter.id
                        )}
                        canShowOptionCount={filtersWithOptionCount.has(
                          filter.id
                        )}
                        canShowOptionBestPrice={filtersWithBestPrice.has(
                          filter.id
                        )}
                        data-testid={testId(`filter-${filter.id}`)}
                        onChange={handleChange(filter)}
                      />
                    );
                  }
                  case FILTER_SUSTAINABILITY: {
                    return (
                      <OptionFilter
                        key={filter.id}
                        isCollapsable
                        caption={
                          <Flex direction="row" spacing={0}>
                            <IconLeaf color="#00a54c" />
                            {filter.title}
                          </Flex>
                        }
                        options={filter.options}
                        isDefaultExpanded={defaultExpandedFiltersArray.has(
                          filter.id
                        )}
                        canShowOptionCount={filtersWithOptionCount.has(
                          filter.id
                        )}
                        canShowOptionBestPrice={filtersWithBestPrice.has(
                          filter.id
                        )}
                        data-testid={testId(`filter-${filter.id}`)}
                        onChange={handleChange(filter)}
                      />
                    );
                  }
                  default: {
                    return null;
                  }
                }
              })}
          </Flex>
        </FilterStrip>
      </Flex>
    );
  }
);

export const Filters = memo(ForwardedFilters);
